// src/Views/ContactForm.js
import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  SPACING_COMPACT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_GROUP,
  SPACING_SECTION_LARGE,
  BORDER_RADIUS,
} from '../constants/layout';
import { FONT_BODY, FONT_TITLE2 } from '../constants/typography';

export default function ContactForm() {
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'contactSubmissions'), {
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        message: contactMessage,
        createdAt: serverTimestamp(),
        isRead: false,
      });

      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
      setIsSubmitted(true);
      setContactName('');
      setContactEmail('');
      setContactPhone('');
      setContactMessage('');
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setIsSubmitted(false);
  };

  if (isSubmitted) {
    return (
      <div className="thank-you-message">
        <span className="material-icons thank-you-icon">check_circle</span>
        <h3 className="thank-you-title">Thank You!</h3>
        <p className="thank-you-text">Someone will get back to you soon.</p>
        <button
          type="button"
          className="send-another-button"
          onClick={handleSendAnother}
        >
          Send Another Message
        </button>

        <style jsx>{`
          .thank-you-message {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: ${SPACING_SECTION_LARGE} ${SPACING_GROUP};
            background-color: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: 12px;
          }

          .thank-you-icon {
            font-size: 64px;
            color: #10B981;
            margin-bottom: ${SPACING_CARD};
          }

          .thank-you-title {
            font-size: ${FONT_TITLE2};
            font-weight: 700;
            color: var(--color-text-primary);
            margin: 0 0 ${SPACING_COMPACT} 0;
          }

          .thank-you-text {
            font-size: ${FONT_BODY};
            color: var(--color-text-secondary);
            margin: 0 0 ${SPACING_GROUP} 0;
          }

          .send-another-button {
            padding: ${SPACING_BUTTON} ${SPACING_GROUP};
            font-size: ${FONT_BODY};
            font-weight: 500;
            background: transparent;
            color: var(--color-accent);
            border: 1px solid var(--color-accent);
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .send-another-button:hover {
            background-color: var(--color-accent);
            color: var(--color-accent-text);
          }
        `}</style>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <input
        type="text"
        placeholder="Your Name *"
        className="form-input"
        value={contactName}
        onChange={(e) => setContactName(e.target.value)}
        required
      />
      <input
        type="email"
        placeholder="Your Email *"
        className="form-input"
        value={contactEmail}
        onChange={(e) => setContactEmail(e.target.value)}
        required
      />
      <input
        type="tel"
        placeholder="Your Phone Number *"
        className="form-input"
        value={contactPhone}
        onChange={(e) => setContactPhone(e.target.value)}
        required
      />
      <textarea
        placeholder="Message (optional)"
        className="form-input form-textarea"
        rows="4"
        value={contactMessage}
        onChange={(e) => setContactMessage(e.target.value)}
      />
      <button type="submit" className="submit-button" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </button>

      <style jsx>{`
        .contact-form {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
        }

        .form-input {
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          font-size: ${FONT_BODY};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          background-color: var(--color-surface);
          color: var(--color-text-primary);
          transition: border-color 0.2s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
          font-family: inherit;
        }

        .submit-button {
          padding: ${SPACING_BUTTON} ${SPACING_GROUP};
          font-size: ${FONT_BODY};
          font-weight: 600;
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .submit-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
        }

        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </form>
  );
}
